/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
'use client';

import { faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalContent } from '@pickleballinc/react-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ConfirmationModal from '@/modules/league/new-components/header/ConfirmationModal';
import { EnterScoreSection } from '@/modules/league/new-components/matches-components/EnterScoreComponent/EnterScoreSection';
import {
  useSaveMatchCancel,
  useSaveMatchScores,
  useSaveMatchUncancel,
} from '@/modules/league/new-components/matches-components/hooks';
import { Scores } from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/MatchCard';
import {
  PlayerBoxFirstTeam,
  PlayerBoxSecondTeam,
} from '@/modules/league/new-components/matches-components/MatchesComponent/MatchCard/PlayerBox';
import { LeagueGameDayHeaderDetails, MatchesPlayers } from '@/modules/league/new-components/types';
import { useLoader } from '@/modules/league/PickleballLoaderContext';
import { initializeScoresArray } from '@/utils/actions/leagues/gameDay/functions/getScores';

interface OpenEnterScoreModal {
  openModal: boolean;
  player: MatchesPlayers;
  team1Wins: number;
  team2Wins: number;
  setTeam1Wins: (wins: number) => void;
  setTeam2Wins: (wins: number) => void;
  setIsModalOpen: (show: boolean) => void;
  isAuthorized: boolean | null;
  setScores: (matchId: string, newScores: Scores) => void;
  scores: Scores;
  isUserAbleToEnterScore: boolean;
  session: LeagueGameDayHeaderDetails;
  userUuid: string | undefined;
  setIsScoreSaved: (save: boolean) => void;
}

const EnterScoreModal = ({
  openModal,
  player,
  team1Wins,
  team2Wins,
  setTeam1Wins,
  setTeam2Wins,
  setIsModalOpen,
  isAuthorized,
  isUserAbleToEnterScore,
  setScores,
  scores,
  session,
  userUuid,
  setIsScoreSaved,
}: OpenEnterScoreModal) => {
  const queryClient = useQueryClient();
  const [showNoticeCancel, setShowNoticeCancel] = useState(false);

  const [showNoticeNoGuaranteed, setShowNoticeNoGuaranteed] = useState(false);
  const [showNoticeSaveUnderMax, setShowNoticeSaveUnderMax] = useState(false);
  const [showNoticeSaveLastGameNotNeeded, setShowNoticeSaveSaveLastGameNotNeeded] = useState(false);
  const [showNoticeSaveLastGameNotPlayed, setShowNoticeSaveSaveLastGameNotPlayed] = useState(false);
  const [showNoticeSaveHigherThenMax, setShowNoticeSaveHigherThenMax] = useState(false);
  const [showNoticeEmptyScore, setShowNoticeEmptyScore] = useState(false);
  const [showNoticeSaveUnderWinBy, setShowNoticeSaveUnderWinBy] = useState(false);
  const [showNoticeSaveWithoutTeamWins, setShowNoticeSaveWithoutTeamWins] = useState(false);

  const [showNoticeUnCancel, setShowNoticeUnCancel] = useState(false);
  const [scoresArray, setScoresArray] = useState(initializeScoresArray(player));

  useEffect(() => {
    setScoresArray(initializeScoresArray(player));
  }, [player]);

  const { mutate: saveMatchCancel } = useSaveMatchCancel();
  const { mutate: saveMatchUncancel } = useSaveMatchUncancel();
  const { mutate: saveMatchScores } = useSaveMatchScores();
  const { setLoading } = useLoader();
  if (!openModal) return null;

  const saveScores = () => {
    if (!player) return;
    setLoading(true);
    const mutationData: { [key: string]: number } = {};
    for (let i = 1; i <= player.MaxGames!; i++) {
      mutationData[`Team1Game${i}Score`] = scoresArray[i - 1]?.team1Score ?? 0;
      mutationData[`Team2Game${i}Score`] = scoresArray[i - 1]?.team2Score ?? 0;
    }

    saveMatchScores(
      { player, mutationData },
      {
        onSuccess: (data) => {
          const newScores = {
            ScoreID: scores.ScoreID,
            Team_1_Game1Score: scoresArray[0]?.team1Score || 0,
            Team_1_Game2Score: scoresArray[1]?.team1Score || 0,
            Team_1_Game3Score: scoresArray[2]?.team1Score || 0,
            Team_1_Game4Score: scoresArray[3]?.team1Score || 0,
            Team_1_Game5Score: scoresArray[4]?.team1Score || 0,
            Team_2_Game1Score: scoresArray[0]?.team2Score || 0,
            Team_2_Game2Score: scoresArray[1]?.team2Score || 0,
            Team_2_Game3Score: scoresArray[2]?.team2Score || 0,
            Team_2_Game4Score: scoresArray[3]?.team2Score || 0,
            Team_2_Game5Score: scoresArray[4]?.team2Score || 0,
          };
          if (data.message) {
            toast.success(data.message);
          }
          setScores(player.ScoreID, newScores);
          setIsScoreSaved(true);
          queryClient.invalidateQueries(['league_game_day_header']);
          queryClient.invalidateQueries(['league_matches']);
        },
        onError: () => {
          toast.error('Something went wrong and we were unable to save scores.');
          setLoading(false);
        },
      }
    );
    setIsModalOpen(false);
  };

  const scoresValid = () => {
    // Case 1: Both Scores Lesser Than Max
    // Case 2: One or both scores higher than max and difference lesser or higher than WinBy
    // Case 3: Number of winned games lesser than Game_Best

    const gameBest = player?.Game_Best!;

    let team1Wins = 0;
    let team2Wins = 0;

    for (let i = 0; i < player!.MaxGames; i++) {
      const score = scoresArray[i];
      const team1Score = score?.team1Score!;
      const team2Score = score?.team2Score!;
      const gameMaxScore = Number(player![('Game' + (i + 1) + '_Max') as keyof MatchesPlayers]);
      const winBy = Number(player![('Game' + (i + 1) + '_WinBy') as keyof MatchesPlayers]);
      if (team1Score || team2Score) {
        if (team1Score < gameMaxScore && team2Score < gameMaxScore) {
          setShowNoticeSaveUnderMax(true);
          return false;
        } else if (team1Score >= gameMaxScore || team2Score >= gameMaxScore) {
          if (Math.max(team1Score, team2Score) - Math.min(team1Score, team2Score) !== winBy) {
            if (
              Math.max(team1Score, team2Score) - Math.min(team1Score, team2Score) < winBy &&
              Math.max(team1Score, team2Score) === gameMaxScore
            ) {
              setShowNoticeSaveUnderWinBy(true);
              return false;
            } else if (
              Math.max(team1Score, team2Score) > gameMaxScore &&
              Math.max(team1Score, team2Score) - Math.min(team1Score, team2Score) !== winBy
            ) {
              setShowNoticeSaveHigherThenMax(true);
              return false;
            }
          } else {
            if (gameBest === 2 || gameBest === 3) {
              if (team1Score > team2Score) {
                team1Wins++;
                setTeam1Wins(team1Wins);
              } else {
                team2Wins++;
                setTeam2Wins(team2Wins);
              }
            }
          }
        }
      } else if (i === 0 && player?.Game1_Max && player?.Game1_Max > 1) {
        setShowNoticeEmptyScore(true);
        return false;
      } else if (i > 0 && i < gameBest) {
        setShowNoticeNoGuaranteed(true);
        return false;
      }
      if (team1Score === player![('Game' + (i + 1) + '_Max') as keyof MatchesPlayers]) {
        team1Wins++;
        setTeam1Wins(team1Wins);
      } else if (team2Score === player![('Game' + (i + 1) + '_Max') as keyof MatchesPlayers]) {
        team2Wins++;
        setTeam2Wins(team2Wins);
      }
    }
    if (team1Wins < gameBest && team2Wins < gameBest && (gameBest === 2 || gameBest === 3)) {
      setShowNoticeSaveWithoutTeamWins(true);
      return false;
    } else if ((team1Wins > gameBest || team2Wins > gameBest) && gameBest === 3) {
      if (team1Wins !== gameBest && team2Wins !== gameBest) {
        setShowNoticeSaveSaveLastGameNotNeeded(true);
      }
      return false;
    }
    return true;
  };

  return (
    <Modal open={openModal}>
      <ModalContent
        className='flex w-[calc(100%-16px)] flex-col gap-4 !overflow-hidden !p-6 !outline-none md:max-w-[430px]'
        key={`player ${player?.T1a_UserID}`}
      >
        <div className='relative'>
          <h2 className='pr-4 text-center text-[16px] font-semibold leading-[22px] text-gray-900'>
            Round {player?.DisplayRoundNumber} : Match {player?.DisplayMatchNumber}
          </h2>
          <div className='mt-4 hidden w-full border-t-[0.5px] border-gray-200 px-2 md:block' />
          <div
            className='absolute -right-2 top-0 flex size-6 items-center justify-center rounded-md border border-solid border-gray-300 bg-white p-1 hover:cursor-pointer hover:bg-gray-100'
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faX} className='text-gray-400' height={20} />
          </div>
        </div>
        <div className='flex items-center justify-center pb-4'>{player?.ScoreTitle}</div>
        <div className='flex w-full flex-col gap-1.5 pb-4'>
          {/* Players Section */}
          <div className='flex justify-center'>
            <PlayerBoxFirstTeam
              player={player}
              session={session}
              userUuid={userUuid}
              teamNumber={1}
            />
          </div>

          {/* Score Section */}
          <EnterScoreSection
            player={player}
            team1Wins={team1Wins}
            team2Wins={team2Wins}
            scoresArray={scoresArray}
            setScoresArray={setScoresArray}
            isAuthorized={isAuthorized}
            isUserAbleToEnterScore={isUserAbleToEnterScore}
          />

          {/* Second Set of Players */}
          <div className='flex justify-center'>
            <PlayerBoxSecondTeam
              player={player}
              session={session}
              userUuid={userUuid}
              teamNumber={2}
            />
          </div>
        </div>
        {isAuthorized || isUserAbleToEnterScore ? (
          <>
            <div className='flex items-center justify-between gap-4'>
              <Button
                variant='link-gray'
                className='flex w-[200px] justify-center !border !border-gray-300 !px-4 !py-2.5 text-[16px] text-sm !text-gray-500 focus:!shadow-[unset]'
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                Close
              </Button>
              <Button
                variant='primary'
                className='flex w-[200px] justify-center !px-4 !py-2.5 text-[16px] focus:!shadow-[unset] !bg-leagues-300'
                onClick={() => {
                  if (player && scoresValid()) {
                    saveScores();
                  }
                }}
              >
                Save
              </Button>
            </div>
            <div className='mt-2 flex items-center justify-center'>
              <Button
                variant='link'
                className='flex w-[200px] justify-center px-4 py-2.5 text-[16px] text-sm !text-leagues-700 focus:!shadow-[unset]'
                onClick={() => {
                  if (player?.DidCancelMatch === false) {
                    setShowNoticeCancel(true);
                  } else {
                    setShowNoticeUnCancel(true);
                  }
                }}
              >
                {player?.DidCancelMatch === false ? 'Cancel' : 'Un-Cancel'} the match
              </Button>
            </div>
          </>
        ) : null}
        <ConfirmationModal
          open={showNoticeSaveLastGameNotNeeded}
          onClose={() => setShowNoticeSaveSaveLastGameNotNeeded(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeSaveSaveLastGameNotNeeded(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message='Last game not needed.'
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeSaveLastGameNotPlayed}
          onClose={() => setShowNoticeSaveSaveLastGameNotPlayed(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeSaveSaveLastGameNotPlayed(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message='Last game is not played.'
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeSaveWithoutTeamWins}
          onClose={() => setShowNoticeSaveWithoutTeamWins(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeSaveWithoutTeamWins(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message={`Single team has not won ${player?.Game_Best} ${player?.Game_Best > 1 ? 'games' : 'game'}.`}
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />

        <ConfirmationModal
          open={showNoticeEmptyScore}
          onClose={() => setShowNoticeEmptyScore(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeEmptyScore(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message='Please fill in the scores. One or more scores are empty.'
          iconColor='error-500'
          confirmButtonText='Save Anyway'
        />
        <ConfirmationModal
          open={showNoticeSaveUnderMax}
          onClose={() => setShowNoticeSaveUnderMax(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeSaveUnderMax(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message={`One or both scores are under ${player?.Game1_Max}`}
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeSaveHigherThenMax}
          onClose={() => setShowNoticeSaveHigherThenMax(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeSaveHigherThenMax(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message={`One or both scores are higher than ${player?.Game1_Max} and win by is not equal to ${player.Game1_WinBy}.`}
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeNoGuaranteed}
          onClose={() => setShowNoticeNoGuaranteed(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeNoGuaranteed(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message={`Guaranteed ${player?.Game_Best} games.`}
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeSaveUnderWinBy}
          onClose={() => setShowNoticeSaveUnderWinBy(false)}
          onConfirm={() => {
            saveScores();
            setShowNoticeSaveUnderWinBy(false);
            setIsModalOpen(false);
          }}
          title='Notice'
          message={`Win by is not equal to ${player?.Game1_WinBy}`}
          confirmButtonText='Save Anyway'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeCancel}
          onClose={() => setShowNoticeCancel(false)}
          onConfirm={() => {
            setLoading(true);
            saveMatchCancel(
              { scoreId: player.ScoreID },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_game_day_header']);
                  queryClient.invalidateQueries(['league_matches']);
                  setIsScoreSaved(true);
                },
                onError: () => {
                  setLoading(false);
                  toast.error('Failed to cancel the match.');
                },
              }
            );
            setShowNoticeCancel(false);
            setIsModalOpen(false);
          }}
          title='Cancel Match'
          message={`Are you sure, canceled games don't count towards stats.`}
          confirmButtonText='Yes'
          iconColor='error-500'
        />
        <ConfirmationModal
          open={showNoticeUnCancel}
          onClose={() => setShowNoticeUnCancel(false)}
          onConfirm={() => {
            setLoading(true);
            saveMatchUncancel(
              { scoreId: player.ScoreID },
              {
                onSuccess: (data) => {
                  if (data.message) {
                    toast.success(data.message);
                  }
                  queryClient.invalidateQueries(['league_game_day_header']);
                  queryClient.invalidateQueries(['league_matches']);
                  setIsScoreSaved(true);
                },
                onError: () => {
                  setLoading(false);
                  toast.error('Failed to un-cancel the match:');
                },
              }
            );
            setShowNoticeUnCancel(false);
            setIsModalOpen(false);
          }}
          title='Un-Cancel Match'
          message={`Are you sure you want to un-cancel this match?`}
          confirmButtonText='Yes'
        />
      </ModalContent>
    </Modal>
  );
};

export default EnterScoreModal;
